/**
 * Leave Reports — filterable, live-preview, exportable.
 *
 * Tabs: Balance Matrix | My Balances | Applications | Holidays | Activity
 * Each tab has its own filter panel, a live data table preview, and PDF/Excel export.
 */
import { useMemo, useState } from "react";
import {
  BarChart2,
  CalendarDays,
  Download,
  FileText,
  Loader2,
  RefreshCw,
  Search,
  TrendingUp,
  Users,
  ListChecks,
} from "lucide-react";

import { useAuth } from "#/context/AuthProvider";
import { leaveApi } from "../api";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeavePagination } from "../components/LeavePagination";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { LeaveStatusBadge } from "../components/LeaveStatusBadge";
import { useAsyncData } from "../hooks";
import { exportToExcel, exportToPdf, exportSectionsToExcel, exportSectionsToPdf } from "../lib/exportUtils";
import type { ExportSection } from "../lib/exportUtils";
import { genderMatches } from "../lib/genderUtils";
import type {
  LeaveApplicationSummary,
  LeaveBalance,
  LeaveBalanceMatrixResponse,
  Holiday,
  LeaveStatus,
  LeaveEntry,
} from "../types";

// ─── tab definitions ──────────────────────────────────────────────────────────

type TabId = "balances" | "my-balances" | "applications" | "holidays" | "activity";

/** All tabs — visibility is filtered at render time based on permissions. */
const ALL_TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }>; requiresViewPerm: boolean }[] = [
  { id: "balances",     label: "Balance Matrix",   icon: BarChart2,   requiresViewPerm: true  },
  { id: "my-balances",  label: "My Balances",      icon: ListChecks,  requiresViewPerm: false },
  { id: "applications", label: "Applications",     icon: TrendingUp,  requiresViewPerm: true  },
  { id: "holidays",     label: "Holidays",         icon: CalendarDays,requiresViewPerm: false },
  { id: "activity",     label: "Leave Activity",   icon: Users,       requiresViewPerm: true  },
];

const CURRENT_YEAR = new Date().getFullYear();

function fmtDate(date?: string | null) {
  if (!date) return "—";
  try {
    return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return date;
  }
}

/** Build a synthetic PageResponse for client-side pagination. */
function toPageResponse<T>(items: T[], page: number, size: number) {
  return {
    content: items.slice(page * size, (page + 1) * size),
    page,
    size,
    totalElements: items.length,
    totalPages: Math.max(1, Math.ceil(items.length / size)),
  };
}

// ─── page ─────────────────────────────────────────────────────────────────────

export default function LeaveReportsPage() {
  const { hasRole, hasAnyRole, hasPermission } = useAuth();
  const isAdmin        = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]);
  const canViewReports = isAdmin || hasPermission("HR_LEAVE_REPORT_VIEW") || hasPermission("HR_LEAVE_MANAGE");

  // Filter tabs based on permission level
  const tabs = ALL_TABS.filter((t) => !t.requiresViewPerm || canViewReports);

  const [tab, setTab] = useState<TabId>(() =>
    canViewReports ? "balances" : "my-balances",
  );

  return (
    <div className="space-y-5">
      <LeavePageHeader
        title="Leave Reports"
        actions={<LeaveBackButton to="/hr/leave" label="Dashboard" />}
      />

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1.5 rounded-2xl border bg-white p-1.5 shadow-sm">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${
              tab === t.id
                ? "bg-emerald-700 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "balances"     && canViewReports && <BalancesReport />}
      {tab === "my-balances"  && <MyBalancesReport />}
      {tab === "applications" && canViewReports && <ApplicationsReport />}
      {tab === "holidays"     && <HolidaysReport />}
      {tab === "activity"     && canViewReports && <ActivityReport />}
    </div>
  );
}

// ─── shared export bar ────────────────────────────────────────────────────────

function ExportBar({
  onPdf,
  onExcel,
  exportingPdf,
  exportingExcel,
  disabled,
  count,
}: {
  onPdf: () => void;
  onExcel: () => void;
  exportingPdf: boolean;
  exportingExcel: boolean;
  disabled: boolean;
  count?: number;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-slate-50 px-4 py-3">
      <span className="text-sm text-slate-500">
        {count !== undefined ? (
          <><span className="font-bold text-slate-800">{count}</span> row(s) in view</>
        ) : ""}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onPdf}
          disabled={disabled || exportingPdf}
          className="inline-flex items-center gap-1.5 rounded-xl border bg-white px-4 py-2 text-sm font-bold hover:bg-red-50 disabled:opacity-40"
        >
          {exportingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-500" />}
          Export PDF
        </button>
        <button
          type="button"
          onClick={onExcel}
          disabled={disabled || exportingExcel}
          className="inline-flex items-center gap-1.5 rounded-xl border bg-white px-4 py-2 text-sm font-bold hover:bg-emerald-50 disabled:opacity-40"
        >
          {exportingExcel ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 text-emerald-600" />}
          Export Excel
        </button>
      </div>
    </div>
  );
}

// ─── 1. Balance Matrix ────────────────────────────────────────────────────────

function BalancesReport() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const { data, loading, error, reload } = useAsyncData(
    () => leaveApi.balanceMatrix(year, page, size, search.trim() || undefined),
    [year, page, size, search],
  );

  // Exports always cover every employee for the year, not just the current on-screen page —
  // the on-screen table stays paginated, but a report that silently dropped everyone past
  // page 1 would be worse than the wide matrix it replaced.
  async function loadFullMatrix(): Promise<LeaveBalanceMatrixResponse | null> {
    if (!data) return null;
    if (data.totalElements <= data.rows.length) return data;
    return leaveApi.balanceMatrix(year, 0, data.totalElements, search.trim() || undefined);
  }

  async function handlePdf() {
    if (!data) return;
    setExportingPdf(true);
    try {
      const full = await loadFullMatrix();
      if (!full) return;
      const sections = buildBalanceExportSections(full);
      await exportSectionsToPdf({ title: `Leave Balance Matrix — ${year}`, sections, filename: `leave-balances-${year}` });
    } finally { setExportingPdf(false); }
  }

  async function handleExcel() {
    if (!data) return;
    setExportingExcel(true);
    try {
      const full = await loadFullMatrix();
      if (!full) return;
      const sections = buildBalanceExportSections(full);
      await exportSectionsToExcel({ title: `Leave Balance Matrix — ${year}`, sections, filename: `leave-balances-${year}` });
    } finally { setExportingExcel(false); }
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid gap-3 rounded-2xl border bg-white p-4 shadow-sm sm:grid-cols-3">
        <FilterField label="Leave Year">
          <input type="number" className="input" min={2020} max={2100} value={year}
            onChange={(e) => setYear(Number(e.target.value))} />
        </FilterField>
        <FilterField label="Search Employee" className="sm:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input className="input pl-9" placeholder="Name, number, department…"
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
          </div>
        </FilterField>
      </div>

      {loading && <LeaveLoading label="Loading balance data…" />}
      {error && <LeaveError message={error} onRetry={reload} />}

      {data && (
        <>
          <ExportBar onPdf={handlePdf} onExcel={handleExcel} exportingPdf={exportingPdf} exportingExcel={exportingExcel}
            disabled={!data.rows.length} count={data.totalElements} />
          <BalanceMatrix data={data} />
          <LeavePagination
            page={{ content: data.rows, page: data.page, size: data.size, totalElements: data.totalElements, totalPages: data.totalPages }}
            onPageChange={(p) => setPage(p)}
            onSizeChange={(s) => { setSize(s); setPage(0); }}
            sizes={[10, 20, 50, 100]}
          />
        </>
      )}
    </div>
  );
}

function BalanceMatrix({ data }: { data: LeaveBalanceMatrixResponse }) {
  if (!data.rows.length) return <EmptyReport message="No employees found." />;
  return (
    <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-xs">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="sticky left-0 bg-slate-50 px-4 py-3 font-bold">Employee</th>
              <th className="px-3 py-3">Function</th>
              {data.columns.map((c) => (
                <th key={c.leaveTypeId} colSpan={4} className="border-l px-3 py-3 text-center font-bold text-slate-600">
                  {c.leaveTypeName}
                  {c.applicableGender !== "ALL" && (
                    <span className="ml-1 text-slate-400">({c.applicableGender === "FEMALE" ? "♀" : "♂"})</span>
                  )}
                </th>
              ))}
            </tr>
            <tr className="border-t">
              <th className="sticky left-0 bg-slate-50 px-4 py-2" />
              <th className="px-3 py-2" />
              {data.columns.map((c) => (
                <>
                  <th key={`${c.leaveTypeId}-e`} className="border-l px-3 py-2 text-emerald-600">Entitled</th>
                  <th key={`${c.leaveTypeId}-u`} className="px-3 py-2 text-amber-600">Used</th>
                  <th key={`${c.leaveTypeId}-f`} className="px-3 py-2 text-red-600">Forfeited</th>
                  <th key={`${c.leaveTypeId}-r`} className="px-3 py-2 text-slate-600">Left</th>
                </>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.map((row) => (
              <tr key={row.employeeId} className="hover:bg-slate-50">
                <td className="sticky left-0 bg-white px-4 py-2.5">
                  <div className="font-bold text-slate-900">{row.employeeName}</div>
                  <div className="text-xs text-slate-400">{row.employeeNumber}</div>
                </td>
                <td className="px-3 py-2.5 text-slate-500">{row.departmentName || "—"}</td>
                {data.columns.map((col) => {
                  const cell = row.cells.find((c) => c.leaveTypeId === col.leaveTypeId);
                  if (!cell || !cell.applicable) return (
                    <>
                      <td key={`${row.employeeId}-${col.leaveTypeId}-e`} className="border-l px-3 py-2.5 text-center text-slate-300">—</td>
                      <td key={`${row.employeeId}-${col.leaveTypeId}-u`} className="px-3 py-2.5 text-center text-slate-300">—</td>
                      <td key={`${row.employeeId}-${col.leaveTypeId}-f`} className="px-3 py-2.5 text-center text-slate-300">—</td>
                      <td key={`${row.employeeId}-${col.leaveTypeId}-r`} className="px-3 py-2.5 text-center text-slate-300">—</td>
                    </>
                  );
                  const remaining = cell.remainingDays ?? 0;
                  const entitled = cell.entitledDays ?? 0;
                  const pct = entitled > 0 ? remaining / entitled : 1;
                  const remColor = pct <= 0 ? "text-red-600 font-black" : pct < 0.3 ? "text-amber-600 font-bold" : "text-emerald-700 font-bold";
                  const forfeited = cell.forfeitedDays ?? 0;
                  return (
                    <>
                      <td key={`${row.employeeId}-${col.leaveTypeId}-e`} className="border-l px-3 py-2.5 text-center">{cell.entitledDays ?? "—"}</td>
                      <td key={`${row.employeeId}-${col.leaveTypeId}-u`} className="px-3 py-2.5 text-center text-slate-500">{cell.usedDays ?? "—"}</td>
                      <td key={`${row.employeeId}-${col.leaveTypeId}-f`} className={`px-3 py-2.5 text-center ${forfeited > 0 ? "font-bold text-red-600" : "text-slate-300"}`}>{forfeited > 0 ? forfeited : "—"}</td>
                      <td key={`${row.employeeId}-${col.leaveTypeId}-r`} className={`px-3 py-2.5 text-center ${remColor}`}>{cell.remainingDays ?? "—"}</td>
                    </>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.totalElements > data.rows.length && (
        <div className="border-t px-4 py-2 text-xs text-slate-400">
          Showing {data.rows.length} of {data.totalElements} employees. Use the employee search to narrow results before exporting.
        </div>
      )}
    </div>
  );
}

// One section per leave type — employees not entitled to that type (e.g. Maternity/Paternity,
// which only apply to one gender) are left out of its section rather than shown as "N/A" rows.
// Balance comes straight from the backend's own remainingDays, not a client-side
// entitled-minus-used guess, so it stays correct if forfeiture/carry-forward rules change.
// No per-section or report-wide totals are drawn — the export states figures per employee only.
function buildBalanceExportSections(data: LeaveBalanceMatrixResponse): ExportSection[] {
  const headers = ["Employee No.", "Employee Name", "Function", "Entitled (days)", "Used (days)", "Balance (days)"];
  return data.columns.map((col) => {
    const rows = data.rows
      .map((row) => ({ row, cell: row.cells.find((c) => c.leaveTypeId === col.leaveTypeId) }))
      .filter(({ cell }) => cell?.applicable)
      .map(({ row, cell }) => [
        row.employeeNumber,
        row.employeeName,
        row.departmentName ?? "",
        cell!.entitledDays ?? 0,
        cell!.usedDays ?? 0,
        cell!.remainingDays ?? 0,
      ]);
    return { heading: col.leaveTypeName, headers, rows };
  });
}

// ─── 2. My Balances ───────────────────────────────────────────────────────────

function MyBalancesReport() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [hideNonApplicable, setHideNonApplicable] = useState(true);

  const balances = useAsyncData(() => leaveApi.myBalances(year), [year]);
  const employee = useAsyncData(() => leaveApi.currentEmployee(), []);
  const allTypes = useAsyncData(() => leaveApi.activeLeaveTypes(), []);

  const employeeGender = employee.data?.gender ?? null;

  const typeGenderMap = useMemo<Record<number, string>>(() => {
    const map: Record<number, string> = {};
    for (const t of allTypes.data ?? []) map[t.id] = t.applicableGender;
    return map;
  }, [allTypes.data]);

  const annotated = useMemo(() => (balances.data ?? []).map((b) => {
    const applicableGender = typeGenderMap[b.leaveTypeId] ?? "ALL";
    const isApplicable = genderMatches(employeeGender, applicableGender);
    return { b, applicableGender, isApplicable };
  }), [balances.data, typeGenderMap, employeeGender]);

  const visible = hideNonApplicable ? annotated.filter((r) => r.isApplicable) : annotated;
  const nonApplicableCount = annotated.filter((r) => !r.isApplicable).length;

  async function handlePdf() {
    if (!balances.data) return;
    setExportingPdf(true);
    try {
      const { headers, rows } = buildMyBalanceExport(visible.map((r) => r.b));
      await exportToPdf({
        title: `My Leave Balances — ${year}`,
        subtitle: `${employee.data?.firstName ?? ""} ${employee.data?.lastName ?? ""}`,
        headers, rows, filename: `my-balances-${year}`,
      });
    } finally { setExportingPdf(false); }
  }

  async function handleExcel() {
    if (!balances.data) return;
    setExportingExcel(true);
    try {
      const { headers, rows } = buildMyBalanceExport(visible.map((r) => r.b));
      await exportToExcel(`My Leave Balances — ${year}`, headers, rows, `my-balances-${year}`);
    } finally { setExportingExcel(false); }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4 rounded-2xl border bg-white p-4 shadow-sm">
        <FilterField label="Leave Year">
          <input type="number" className="input w-32" min={2020} max={2100} value={year}
            onChange={(e) => setYear(Number(e.target.value))} />
        </FilterField>
        {nonApplicableCount > 0 && (
          <button type="button" onClick={() => setHideNonApplicable((v) => !v)}
            className="rounded-xl border px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50">
            {hideNonApplicable
              ? `Show all (${nonApplicableCount} not applicable for your gender)`
              : "Hide non-applicable"}
          </button>
        )}
      </div>

      {balances.loading && <LeaveLoading label="Loading your balances…" />}
      {balances.error && <LeaveError message={balances.error} onRetry={balances.reload} />}

      {balances.data && (
        <>
          <ExportBar onPdf={handlePdf} onExcel={handleExcel} exportingPdf={exportingPdf} exportingExcel={exportingExcel}
            disabled={!visible.length} count={visible.length} />
          <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Leave Type</th>
                  <th className="px-4 py-3 text-right">Entitled</th>
                  <th className="px-4 py-3 text-right">Carried Fwd</th>
                  <th className="px-4 py-3 text-right">Pending</th>
                  <th className="px-4 py-3 text-right">Used</th>
                  <th className="px-4 py-3 text-right">Remaining</th>
                  <th className="px-4 py-3">Usage</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {visible.map(({ b, applicableGender, isApplicable }) => {
                  const pct = b.entitledDays > 0 ? Math.min(100, Math.round(((b.usedDays + b.pendingDays) / b.entitledDays) * 100)) : 0;
                  return (
                    <tr key={b.id} className={`${isApplicable ? "hover:bg-slate-50" : "bg-slate-50 opacity-60"}`}>
                      <td className="px-4 py-3">
                        <div className={`font-bold ${isApplicable ? "text-slate-900" : "text-slate-400"}`}>
                          {b.leaveTypeName}
                        </div>
                        {applicableGender !== "ALL" && (
                          <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-xs font-bold ${
                            applicableGender === "FEMALE" ? "bg-pink-100 text-pink-700" : "bg-blue-100 text-blue-700"
                          }`}>
                            {applicableGender === "FEMALE" ? "♀ Female only" : "♂ Male only"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">{b.entitledDays}</td>
                      <td className="px-4 py-3 text-right text-blue-600">{b.broughtForwardDays || "—"}</td>
                      <td className="px-4 py-3 text-right text-amber-600">{b.pendingDays || "—"}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{b.usedDays}</td>
                      <td className={`px-4 py-3 text-right font-black ${isApplicable ? "text-emerald-700" : "text-slate-400"}`}>
                        {b.remainingDays}
                      </td>
                      <td className="px-4 py-3">
                        {isApplicable ? (
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100">
                              <div className={`h-full rounded-full ${pct >= 80 ? "bg-red-400" : pct >= 50 ? "bg-amber-400" : "bg-emerald-400"}`}
                                style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-slate-400">{pct}%</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">N/A</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!visible.length && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                    {balances.data.length === 0
                      ? `No balance records for ${year}.`
                      : "No applicable leave types. Toggle above to view all."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function buildMyBalanceExport(balances: LeaveBalance[]) {
  const headers = ["Leave Type", "Year", "Entitled", "Brought Fwd", "Pending", "Used", "Forfeited", "Remaining"];
  const rows = balances.map((b) => [b.leaveTypeName, b.leaveYear, b.entitledDays, b.broughtForwardDays, b.pendingDays, b.usedDays, b.forfeitedDays, b.remainingDays]);
  return { headers, rows };
}

// ─── 3. Applications ──────────────────────────────────────────────────────────

const ALL_STATUSES: LeaveStatus[] = ["DRAFT", "PENDING_APPROVAL", "APPROVED", "REJECTED", "CANCELLED"];

function ApplicationsReport() {
  const [statusFilter, setStatusFilter] = useState<LeaveStatus | "ALL">("ALL");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const { data, loading, error, reload } = useAsyncData(() => leaveApi.myApplications(0, 250), []);

  const filtered = useMemo<LeaveApplicationSummary[]>(() => {
    if (!data?.content) return [];
    return data.content.filter((a) => {
      const matchStatus = statusFilter === "ALL" || a.status === statusFilter;
      const q = search.trim().toLowerCase();
      const matchSearch = !q || a.applicationNo.toLowerCase().includes(q) || a.leaveTypeName.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [data?.content, statusFilter, search]);

  const paged = useMemo(() => toPageResponse(filtered, page, size), [filtered, page, size]);

  async function handlePdf() {
    setExportingPdf(true);
    try {
      const { headers, rows } = buildApplicationExport(filtered);
      await exportToPdf({ title: "Leave Applications Report", headers, rows, filename: "applications-report" });
    } finally { setExportingPdf(false); }
  }

  async function handleExcel() {
    setExportingExcel(true);
    try {
      const { headers, rows } = buildApplicationExport(filtered);
      await exportToExcel("Leave Applications Report", headers, rows, "applications-report");
    } finally { setExportingExcel(false); }
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid gap-3 rounded-2xl border bg-white p-4 shadow-sm sm:grid-cols-2">
        <FilterField label="Status">
          <select className="input" value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as LeaveStatus | "ALL"); setPage(0); }}>
            <option value="ALL">All statuses</option>
            {ALL_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </FilterField>
        <FilterField label="Search">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input className="input pl-9" placeholder="Application no. or leave type…"
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
          </div>
        </FilterField>
      </div>

      {loading && <LeaveLoading label="Loading applications…" />}
      {error && <LeaveError message={error} onRetry={reload} />}

      {data && (
        <>
          <ExportBar onPdf={handlePdf} onExcel={handleExcel} exportingPdf={exportingPdf} exportingExcel={exportingExcel}
            disabled={!filtered.length} count={filtered.length} />
          <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Application #</th>
                    <th className="px-4 py-3">Leave Type</th>
                    <th className="px-4 py-3">Start</th>
                    <th className="px-4 py-3">End</th>
                    <th className="px-4 py-3">Days</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Step</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paged.content.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-xs font-bold text-slate-700">{a.applicationNo}</td>
                      <td className="px-4 py-3 font-medium">{a.leaveTypeName}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(a.startDate)}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(a.endDate)}</td>
                      <td className="px-4 py-3"><span className="rounded-lg bg-slate-100 px-2 py-0.5 font-black">{a.requestedDays}</span></td>
                      <td className="px-4 py-3"><LeaveStatusBadge value={a.status} /></td>
                      <td className="px-4 py-3 text-xs text-slate-500">{a.currentStepName || "—"}</td>
                    </tr>
                  ))}
                  {!filtered.length && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No applications match your filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <LeavePagination
            page={paged}
            onPageChange={setPage}
            onSizeChange={(s) => { setSize(s); setPage(0); }}
            sizes={[10, 20, 50]}
          />
        </>
      )}
    </div>
  );
}

function buildApplicationExport(apps: LeaveApplicationSummary[]) {
  const headers = ["Application No.", "Leave Type", "Start Date", "End Date", "Days", "Status", "Step", "Submitted"];
  const rows = apps.map((a) => [a.applicationNo, a.leaveTypeName, a.startDate, a.endDate, a.requestedDays, a.status, a.currentStepName ?? "", a.submittedAt ?? ""]);
  return { headers, rows };
}

// ─── 4. Holidays ─────────────────────────────────────────────────────────────

function HolidaysReport() {
  const [activeFilter, setActiveFilter] = useState<"ALL" | "active" | "inactive">("active");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const { data, loading, error, reload } = useAsyncData(() => leaveApi.holidays(0, 200), []);

  const filtered = useMemo<Holiday[]>(() => {
    if (!data?.content) return [];
    return data.content.filter((h) => {
      const matchActive = activeFilter === "ALL" || (activeFilter === "active" ? h.active : !h.active);
      const q = search.trim().toLowerCase();
      const matchSearch = !q || h.name.toLowerCase().includes(q) || h.holidayDate.includes(q);
      return matchActive && matchSearch;
    });
  }, [data?.content, activeFilter, search]);

  const paged = useMemo(() => toPageResponse(filtered, page, size), [filtered, page, size]);

  async function handlePdf() {
    setExportingPdf(true);
    try {
      const { headers, rows } = buildHolidayExport(filtered);
      await exportToPdf({ title: "Public Holidays Calendar", headers, rows, filename: "holidays" });
    } finally { setExportingPdf(false); }
  }

  async function handleExcel() {
    setExportingExcel(true);
    try {
      const { headers, rows } = buildHolidayExport(filtered);
      await exportToExcel("Public Holidays Calendar", headers, rows, "holidays");
    } finally { setExportingExcel(false); }
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 rounded-2xl border bg-white p-4 shadow-sm sm:grid-cols-2">
        <FilterField label="Status">
          <select className="input" value={activeFilter}
            onChange={(e) => { setActiveFilter(e.target.value as typeof activeFilter); setPage(0); }}>
            <option value="ALL">All</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
        </FilterField>
        <FilterField label="Search">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input className="input pl-9" placeholder="Holiday name or date…"
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
          </div>
        </FilterField>
      </div>

      {loading && <LeaveLoading label="Loading holidays…" />}
      {error && <LeaveError message={error} onRetry={reload} />}

      {data && (
        <>
          <ExportBar onPdf={handlePdf} onExcel={handleExcel} exportingPdf={exportingPdf} exportingExcel={exportingExcel}
            disabled={!filtered.length} count={filtered.length} />
          <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Holiday</th>
                  <th className="px-4 py-3">Recurring</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paged.content.map((h) => (
                  <tr key={h.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-bold text-slate-700">{fmtDate(h.holidayDate)}</td>
                    <td className="px-4 py-3">
                      <div className="font-bold text-slate-900">{h.name}</div>
                      {h.description && <div className="text-xs text-slate-400">{h.description}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {h.recurringAnnually && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-700">Annual</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${h.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                        {h.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">No holidays match your filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <LeavePagination
            page={paged}
            onPageChange={setPage}
            onSizeChange={(s) => { setSize(s); setPage(0); }}
            sizes={[10, 20, 50]}
          />
        </>
      )}
    </div>
  );
}

function buildHolidayExport(holidays: Holiday[]) {
  const headers = ["Date", "Holiday Name", "Description", "Recurring", "Active"];
  const rows = holidays.map((h) => [h.holidayDate, h.name, h.description ?? "", h.recurringAnnually ? "Yes" : "No", h.active ? "Yes" : "No"]);
  return { headers, rows };
}

// ─── 5. Leave Activity ────────────────────────────────────────────────────────

function ActivityReport() {
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const { data, loading, error, reload } = useAsyncData(() => leaveApi.whoIsOnLeave(), []);

  const combined = useMemo<(LeaveEntry & { section: string })[]>(() => {
    if (!data) return [];
    return [
      ...data.currentlyOnLeave.map((e) => ({ ...e, section: "Currently on Leave" })),
      ...data.pendingApproval.map((e) => ({ ...e, section: "Pending Approval" })),
      ...data.upcomingLeave.map((e) => ({ ...e, section: "Upcoming (next 30 days)" })),
    ];
  }, [data]);

  async function handlePdf() {
    setExportingPdf(true);
    try {
      const { headers, rows } = buildActivityExport(combined);
      await exportToPdf({ title: "Current Leave Activity", headers, rows, filename: "leave-activity" });
    } finally { setExportingPdf(false); }
  }

  async function handleExcel() {
    setExportingExcel(true);
    try {
      const { headers, rows } = buildActivityExport(combined);
      await exportToExcel("Current Leave Activity", headers, rows, "leave-activity");
    } finally { setExportingExcel(false); }
  }

  const sections = [
    { key: "currentlyOnLeave", label: "Currently on Leave", color: "bg-emerald-50 border-emerald-200 text-emerald-800", items: data?.currentlyOnLeave ?? [] },
    { key: "pendingApproval",  label: "Pending Approval",  color: "bg-amber-50 border-amber-200 text-amber-800",    items: data?.pendingApproval ?? [] },
    { key: "upcomingLeave",    label: "Upcoming Leave",    color: "bg-blue-50 border-blue-200 text-blue-800",        items: data?.upcomingLeave ?? [] },
  ] as const;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 rounded-2xl border bg-white px-4 py-3 shadow-sm">
        <span className="text-sm text-slate-500 flex-1">Shows real-time leave activity. Refresh to get the latest data.</span>
        <button type="button" onClick={reload}
          className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-bold hover:bg-slate-50">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {loading && <LeaveLoading label="Loading leave activity…" />}
      {error && <LeaveError message={error} onRetry={reload} />}

      {data && (
        <>
          {/* Summary chips */}
          <div className="grid gap-3 sm:grid-cols-3">
            {sections.map((s) => (
              <div key={s.key} className={`rounded-2xl border px-4 py-3 ${s.color}`}>
                <div className="text-xs font-bold uppercase tracking-wide">{s.label}</div>
                <div className="mt-1 text-2xl font-black">{s.items.length}</div>
              </div>
            ))}
          </div>

          <ExportBar onPdf={handlePdf} onExcel={handleExcel} exportingPdf={exportingPdf} exportingExcel={exportingExcel}
            disabled={!combined.length} count={combined.length} />

          {sections.map((s) => s.items.length > 0 && (
            <div key={s.key} className="overflow-hidden rounded-2xl border bg-white shadow-sm">
              <div className={`flex items-center gap-2 border-b px-5 py-3 ${s.color}`}>
                <span className="font-black">{s.label}</span>
                <span className="rounded-full bg-white/60 px-2 py-0.5 text-xs font-bold">{s.items.length}</span>
              </div>
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Employee</th>
                    <th className="px-4 py-2">Function</th>
                    <th className="px-4 py-2">Leave Type</th>
                    <th className="px-4 py-2">Start</th>
                    <th className="px-4 py-2">End</th>
                    <th className="px-4 py-2">Days</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {s.items.map((e) => (
                    <tr key={e.applicationId} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-bold text-slate-900">{e.employeeName}</td>
                      <td className="px-4 py-2.5 text-slate-500">{e.departmentName || "—"}</td>
                      <td className="px-4 py-2.5">{e.leaveTypeName}</td>
                      <td className="px-4 py-2.5 text-slate-600">{fmtDate(e.startDate)}</td>
                      <td className="px-4 py-2.5 text-slate-600">{fmtDate(e.endDate)}</td>
                      <td className="px-4 py-2.5"><span className="rounded-lg bg-slate-100 px-2 py-0.5 font-black">{e.requestedDays}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function buildActivityExport(items: (LeaveEntry & { section: string })[]) {
  const headers = ["Section", "Employee", "Function", "Leave Type", "Start", "End", "Days"];
  const rows = items.map((e) => [e.section, e.employeeName, e.departmentName ?? "", e.leaveTypeName, e.startDate, e.endDate, e.requestedDays]);
  return { headers, rows };
}

// ─── shared helpers ───────────────────────────────────────────────────────────

function FilterField({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`space-y-1.5 ${className ?? ""}`}>
      <label className="block text-xs font-bold uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}

function EmptyReport({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border bg-white p-10 text-center text-sm text-slate-400 shadow-sm">
      {message}
    </div>
  );
}
